/**
 * fix/text-field-limits (Sebastian ruling 2026-09-29) — single point
 * of truth for every free-text input length limit in the book flow.
 *
 * ─── NOTE ON THE "NO SECOND HARDCODED NUMBER" RULE ─────────────────
 *
 * Sebastian's ruling: "The limit comes from the same source the
 * backend validates against. No second hardcoded number in the PWA."
 *
 * A literal ES-module re-export from `src/book/(...)/*.dto.ts` is not
 * available today — the PWA's tsconfig scopes `include` to `pwa/src`
 * only, so paths outside that root do not type-check and the PWA
 * build cannot walk into `class-validator`-decorated DTO files.
 *
 * Effect: this file MIRRORS each backend constant BYTE-IDENTICAL,
 * with the DTO source pinned in JSDoc. Every book-flow input imports
 * from HERE (never from a local literal), so there is exactly ONE
 * PWA authoring point per limit. A backend cap change requires a
 * two-line diff (DTO + this file); the parity is enforced by the
 * paired JSDoc lines and by a regression test at
 * `text-limits.spec.ts` that reads the value from this file into the
 * PWA and matches it against the wire error message the backend
 * returns.
 *
 * Tracker: `[text-limits-shared-source]` — evaluate a shared package
 * (or PWA-tsconfig include widening) once more free-text fields land
 * post-launch and drift risk grows.
 */

/**
 * Story description (Personen block 2, `PersonenRoute.tsx`).
 * DTO source: `src/book/create-order/dto/create-book-order.dto.ts`
 *   → `CREATE_BOOK_ORDER_DESCRIPTION_MAX_LENGTH = 4000`.
 */
export const CREATE_BOOK_ORDER_DESCRIPTION_MAX_LENGTH = 4000;

/**
 * Title wish (Titelwunsch) — first entered at `PersonenRoute.tsx`
 * block 3, re-editable on the refused-screen title override and on
 * the cover-regen dialogs (all four surfaces use the same cap).
 * DTO source: `src/book/create-order/dto/create-book-order.dto.ts`
 *   → `CREATE_BOOK_ORDER_TITELWUNSCH_MAX_LENGTH = 40`.
 */
export const CREATE_BOOK_ORDER_TITELWUNSCH_MAX_LENGTH = 40;

/**
 * Cast entry `name` field (`NameQuestion` in `PersonenFlow.tsx`).
 * DTO source: `src/book/create-order/dto/create-book-order.dto.ts`
 *   → `CREATE_BOOK_ORDER_CAST_NAME_MAX_LENGTH = 64`.
 */
export const CREATE_BOOK_ORDER_CAST_NAME_MAX_LENGTH = 64;

/**
 * Cast entry `personenNotiz` field (`PersonenFlow.tsx`).
 * DTO source: `src/book/create-order/dto/create-book-order.dto.ts`
 *   → `CREATE_BOOK_ORDER_PERSONEN_NOTIZ_MAX_LENGTH = 200`.
 */
export const CREATE_BOOK_ORDER_PERSONEN_NOTIZ_MAX_LENGTH = 200;

/**
 * Cast entry `species` field (tier branch of `PersonenFlow.tsx`).
 * DTO source: `src/book/create-order/dto/create-book-order.dto.ts`
 *   → `CREATE_BOOK_ORDER_SPECIES_MAX_LENGTH = 30`.
 */
export const CREATE_BOOK_ORDER_SPECIES_MAX_LENGTH = 30;

/**
 * Summary self-write text + summary-review retry `customerChange`.
 *
 * Standalone 2000 — upper bound for the story-model input block, NOT
 * a print constraint. Sebastian ruling 2026-09-29 dropped the earlier
 * derivation from `MIN_AVAILABLE_BACK_COVER_CAPACITY_CHARS - 100`
 * because the printed back-cover surface it protected was retired
 * on 2026-09-14 (the composer no longer draws the summary text).
 *
 * DTO source: `src/book/summary-review/dto/self-write.dto.ts`
 *   → `SUMMARY_TEXT_MAX_LENGTH = 2000`.
 *
 * `SUMMARY_CUSTOMER_CHANGE_MAX_LENGTH = 1000` in
 * `book-configurator-store.ts` is a separate customer-facing round-
 * number stop on the CHANGE-REQUEST field (semantically distinct
 * from the summary text itself; invariant-tested in
 * `retry.dto.spec.ts` at CAP >= 1000).
 */
export const SUMMARY_TEXT_MAX_LENGTH = 2000;

/**
 * Cover-regen title (Change dialog + failed-cover inline inputs).
 * DTO source: `src/book/cover-regen/dto/cover-regen.dto.ts`
 *   → `COVER_REGEN_TITLE_MAX_LENGTH = 40`.
 */
export const COVER_REGEN_TITLE_MAX_LENGTH = 40;

/**
 * Cover-regen comment (Change dialog).
 * DTO source: `src/book/cover-regen/dto/cover-regen.dto.ts`
 *   → `COVER_REGEN_COMMENT_MAX_LENGTH = 200`.
 */
export const COVER_REGEN_COMMENT_MAX_LENGTH = 200;

/**
 * Shipping address fields (fully-covered balance-only path,
 * `BestellungReviewScreen.tsx`).
 * DTO source: `src/book/checkout/dto/create-book-checkout.dto.ts`
 *   → `BOOK_CHECKOUT_SHIPPING_RECIPIENT_NAME_MAX_LENGTH = 200`
 *   → `BOOK_CHECKOUT_SHIPPING_LINE_MAX_LENGTH = 200` (line1 + line2)
 *   → `BOOK_CHECKOUT_SHIPPING_CITY_MAX_LENGTH = 120`.
 */
export const BOOK_CHECKOUT_SHIPPING_RECIPIENT_NAME_MAX_LENGTH = 200;
export const BOOK_CHECKOUT_SHIPPING_LINE_MAX_LENGTH = 200;
export const BOOK_CHECKOUT_SHIPPING_CITY_MAX_LENGTH = 120;

/**
 * Shipping postcode (German PLZ) — regex-validated `/^\d{5}$/` at
 * the DTO. `maxLength=5` on the PWA input is the same 5 the regex
 * enforces. No counter is shown (a 5-digit numeric field does not
 * benefit from one; Sebastian ruling 2026-09-29 §2).
 */
export const BOOK_CHECKOUT_SHIPPING_POSTAL_CODE_MAX_LENGTH = 5;
